// Eskom PARCC — pure filter + search for the Transactions register.
//
// Ported from the monolith's `filteredTransactions()` (index.html): each select
// narrows by exact match, the free-text search is a case-insensitive substring
// over the visible/searchable fields. Kept pure (no React, no fetching) so it is
// unit-testable in isolation. Compliance is supplied per-row by the caller —
// it is computed once by the page (via `effectiveCompliance`) so this helper
// stays free of domain maths and never recomputes per keystroke.
import type { Transaction } from '../../data/types';
import { canonicalDivision } from '../../domain/divisions';

export interface TransactionFilters {
  dataset: string;
  year: string;
  division: string;
  period: string;
  compliance: string;
  /** Free-text search across ref/supplier/division/mechanism (+ more). */
  search: string;
}

export const EMPTY_TRANSACTION_FILTERS: TransactionFilters = {
  dataset: '',
  year: '',
  division: '',
  period: '',
  compliance: '',
  search: '',
};

/**
 * A transaction paired with its already-computed effective compliance level, so
 * filtering by compliance and searching do not recompute domain maths per row.
 */
export interface TransactionRow {
  tx: Transaction;
  compliance: string;
}

/** Lowercased haystack of the searchable fields, mirroring the monolith. */
function haystack(tx: Transaction): string {
  return [
    tx.referenceNo,
    tx.baseNo,
    tx.recordLabel,
    tx.supplier,
    tx.mechanism,
    tx.dataset,
    tx.division,
    tx.period,
    tx.year,
    tx.csdNumber,
    tx.valueDisplay,
    tx.link,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
}

/**
 * Narrow `rows` by the active filters. Each non-empty select narrows by exact
 * match (dataset/division/period on the transaction, year against the `year`
 * field, compliance against the pre-computed level); the search term narrows by
 * case-insensitive substring across {@link haystack}. Input order is preserved
 * (the DataTable owns sorting), so this composes cleanly with column sort.
 */
export function filterTransactions(
  rows: readonly TransactionRow[],
  filters: TransactionFilters,
): TransactionRow[] {
  const q = filters.search.trim().toLowerCase();
  return rows.filter(({ tx, compliance }) => {
    if (filters.dataset && tx.dataset !== filters.dataset) return false;
    if (filters.year && (tx.year ?? '') !== filters.year) return false;
    if (filters.division && canonicalDivision(tx.division) !== filters.division) return false;
    if (filters.period && (tx.period ?? '') !== filters.period) return false;
    if (filters.compliance && compliance !== filters.compliance) return false;
    if (q && !haystack(tx).includes(q)) return false;
    return true;
  });
}

/** Sorted, de-duplicated list of the non-blank values of `pick` across `rows`. */
export function distinctValues(
  rows: readonly TransactionRow[],
  pick: (tx: Transaction) => string | undefined,
): string[] {
  const seen = new Set<string>();
  for (const { tx } of rows) {
    const v = pick(tx);
    if (v) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}
